// Components==============
import { SoundsExerciseContext } from 'components/Exercises/SoundsExercise/SoundsExercise';
import Column from 'components/UI/ExerciseColumn/Column';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { HomeworkContext } from 'screens/Homework/Homework';
import { shake } from 'utilities/framerAnimations';
import Row from '../Row/Row';
import './Category.module.scss';
// =========================

type Props = {
    name: string;
    phoneme?: string;
    pivot?: string | null;
    id?: number;
    image?: string | null;
    words?: Words[];
    column?: number;
    isSound?: 0 | 1;
};

const categoryVariants = {
    visible: {
        opacity: 1,
    },
    hidden: {
        opacity: 0,
    },
};

export default function Category({
    name,
    phoneme,
    pivot,
    id,
    image,
    words,
    column,
    isSound,
}: Props) {
    const { background } = useContext(HomeworkContext);

    const {
        activeTileBar,
        useWords,
        useImages,
        useCategories,
        answer,
        useTiles,
        activeInput,
        givenAnswers,
    } = useContext(SoundsExerciseContext);
    const [amountOfRows, setAmountOfRows] = useState(1);
    const [triggerAnimation, setTriggerAnimation] = useState(false);

    const activeCondition = useTiles
        ? activeTileBar === 'initial' ||
          activeTileBar.includes(`column-${column}`)
        : activeInput === 'initial' || activeInput === name;

    const rows = Array.from(Array(amountOfRows).keys());

    const wordsInCategory = words ? words.map((word) => word.name) : undefined;

    useEffect(() => {
        setTimeout(() => {
            setTriggerAnimation(false);
        }, 1000);
    }, [triggerAnimation]);

    const columnProps = {
        image,
        phoneme,
        useWords: useCategories ? 1 : useWords,
        useImages: useCategories ? 1 : useImages,
        useCategories,
        isSound,
        background,
    };

    // set rows if answers are already given
    useEffect(() => {
        if (useCategories) {
            setAmountOfRows(
                (givenAnswers?.filter(
                    (a) =>
                        wordsInCategory?.includes(a.correctText) &&
                        a.category_id === id
                ).length || 0) + 1
            );
        }
    }, []);

    if (useCategories) {
        return (
            <motion.div
                animate={triggerAnimation ? { x: shake } : { x: 0 }}
                initial={{ x: 0 }}
                styleName="wrapper"
                style={{
                    opacity: activeCondition ? 1 : 0.3,
                    width: useImages || image ? 280 : '',
                }}
            >
                <Column name={name} {...columnProps}>
                    {rows.map((e, i) => (
                        <Row
                            key={e}
                            id={`column-${column}-row-${i + 1}`}
                            setAmountOfRows={setAmountOfRows}
                            wordsInCategory={wordsInCategory}
                            setTriggerAnimation={setTriggerAnimation}
                            name={name}
                            withImage={!!(useImages || image)}
                            category_id={id}
                        />
                    ))}
                </Column>
            </motion.div>
        );
    }

    return (
        <motion.div
            styleName="wrapper single-item"
            variants={categoryVariants}
            style={{
                pointerEvents: name === answer?.name ? 'initial' : 'none',
            }}
            animate={name === answer?.name ? 'visible' : 'hidden'}
            initial="hidden"
            exit="hidden"
        >
            <Column name={pivot || name} showSpeaker={1} {...columnProps}>
                <Row
                    name={name}
                    withImage={!!(useImages || image)}
                    id="column-1-row-1"
                />
            </Column>
        </motion.div>
    );
}
